import { useMemo, useState } from 'react';
import { CreditCard, HandCoins, MapPin, MessageCircle, Phone } from 'lucide-react';
import { ServiceModal } from '@/components/ServiceModal';
import { openWhatsApp } from '@/lib/whatsapp';
import { getStoreMapUrls } from './getStoreMapUrls';
import './home.css';

const FINANCING_ID = '65dcb7898eca108d87c91dbc';
const AVALIACAO_ID = '65dcb7548eca108d87c91dbb';
const CONSIGNACAO_ID = '65dcb7c58eca108d87c91dbd';

interface HomeCtaSectionsProps {
  config: any;
  store: any;
}

export function HomeCtaSections({ config, store }: HomeCtaSectionsProps) {
  const [financingOpen, setFinancingOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);

  const financingService = useMemo(
    () => store.services?.find((s: any) => s.service === FINANCING_ID),
    [store.services]
  );
  const sellService = useMemo(
    () =>
      store.services?.find((s: any) => s.service === AVALIACAO_ID) ||
      store.services?.find((s: any) => s.service === CONSIGNACAO_ID),
    [store.services]
  );

  const { embedUrl, directionsUrl } = getStoreMapUrls(store.place);
  const address = store.place?.address;

  return (
    <>
      {sellService && (
        <section id="vender" className="wl7-home-cta">
          <div className="wl7-container">
            <div className="wl7-home-cta-inner">
              <div>
                <p className="wl7-eyebrow">Troca e avaliação</p>
                <h2 className="wl7-h2">Quer vender seu veículo?</h2>
                <p className="wl7-lead mt-3 max-w-xl">
                  Avaliamos com cuidado técnico e valorizamos veículos com boa procedência.
                  Envie os dados e nossa equipe retorna rápido.
                </p>
              </div>
              <button
                type="button"
                className="wl7-home-cta-btn"
                onClick={() => setSellOpen(true)}
              >
                <HandCoins className="h-5 w-5" />
                Avaliar meu veículo
              </button>
            </div>
          </div>
          <ServiceModal
            service={sellService}
            config={config}
            isOpen={sellOpen}
            onOpenChange={setSellOpen}
          />
        </section>
      )}

      {financingService && (
        <section id="financiamento" className="wl7-home-cta wl7-home-cta--band">
          <div className="wl7-container">
            <div className="wl7-home-cta-inner">
              <div>
                <p className="wl7-eyebrow">Financiamento</p>
                <h2 className="wl7-h2">Financie com condições especiais</h2>
                <p className="wl7-lead mt-3 max-w-xl">
                  Envie sua proposta e nossa equipe analisa as melhores opções para o seu perfil.
                </p>
              </div>
              <button
                type="button"
                className="wl7-home-cta-btn wl7-home-cta-btn--light"
                onClick={() => setFinancingOpen(true)}
              >
                <CreditCard className="h-5 w-5" />
                Simular financiamento
              </button>
            </div>
          </div>
          <ServiceModal
            service={financingService}
            config={config}
            isOpen={financingOpen}
            onOpenChange={setFinancingOpen}
          />
        </section>
      )}

      {(address || embedUrl) && (
        <section id="localizacao" className="wl7-home-location">
          <div className="wl7-container">
            <div className="wl7-home-location-panel">
              <div className="wl7-home-location-copy">
                <p className="wl7-eyebrow">Localização</p>
                <h2 className="wl7-h2">Venha nos visitar</h2>
                <p className="wl7-lead wl7-home-location-lead">
                  Conheça nossa loja e encontre o veículo ideal com atendimento presencial.
                </p>

                {address && (
                  <p className="wl7-home-location-address">
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{address}</span>
                  </p>
                )}

                <div className="wl7-home-location-actions">
                  {directionsUrl && (
                    <a
                      href={directionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wl7-home-cta-btn"
                    >
                      <MapPin className="h-4 w-4" />
                      Como chegar
                    </a>
                  )}
                  {store.whatsapp?.ios && (
                    <button
                      type="button"
                      className="wl7-home-cta-btn wl7-home-cta-btn--outline"
                      onClick={() => openWhatsApp(store.whatsapp.ios)}
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </button>
                  )}
                  {store.phone?.ios && (
                    <a href={store.phone.ios} className="wl7-home-cta-btn wl7-home-cta-btn--outline">
                      <Phone className="h-4 w-4" />
                      {store.phone.formatted || 'Ligar'}
                    </a>
                  )}
                </div>
              </div>

              {embedUrl && (
                <div className="wl7-home-location-map">
                  <iframe
                    title={`Mapa — ${store.company || config.name}`}
                    src={embedUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
